/**
 * TraitsAssignment Component
 *
 * Character Creation Step 3: Assign the six trait modifiers (+2, +1, +1, 0, 0, -1)
 * to character traits (Agility, Strength, Finesse, Instinct, Presence, Knowledge)
 *
 * Features:
 * - Dropdown selectors for each trait
 * - Real-time modifier tracking
 * - "Use Suggested" quick-fill from class data
 * - Mobile-responsive layout
 * - Accessibility compliant
 */

import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import {
  AVAILABLE_MODIFIERS,
  TRAIT_DESCRIPTIONS,
  TraitAssignment,
  ValidationState,
  calculateRemainingModifiers,
  validateTraitAssignments,
  formatModifier,
  getTraitKeys
} from '../../utils/traitValidation';
import { findClassById } from '../../utils/classDataLoader';
import styles from './TraitsAssignment.module.css';

interface TraitsAssignmentProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

/**
 * TraitsAssignment component for character creation
 */
const TraitsAssignment: React.FC<TraitsAssignmentProps> = ({
  characterData,
  onDataChange
}) => {
  // Local state for trait assignments
  const [assignments, setAssignments] = useState<TraitAssignment>({
    agility: null,
    strength: null,
    finesse: null,
    instinct: null,
    presence: null,
    knowledge: null
  });

  // Track validation state
  const [validation, setValidation] = useState<ValidationState>({
    isValid: false,
    errors: [],
    warnings: [],
    hints: []
  });

  // Track remaining modifiers
  const [remainingModifiers, setRemainingModifiers] = useState<number[]>(
    [...AVAILABLE_MODIFIERS]
  );

  // Track suggested traits from class data
  const [suggestedTraits, setSuggestedTraits] = useState<Record<string, number> | null>(
    null
  );

  // Initialize from characterData if traits already assigned
  useEffect(() => {
    if (characterData.traits) {
      const traitValues = characterData.traits as Record<string, number>;
      const newAssignments: TraitAssignment = {
        agility: traitValues.agility ?? null,
        strength: traitValues.strength ?? null,
        finesse: traitValues.finesse ?? null,
        instinct: traitValues.instinct ?? null,
        presence: traitValues.presence ?? null,
        knowledge: traitValues.knowledge ?? null
      };
      setAssignments(newAssignments);
      updateValidation(newAssignments);
      updateRemainingModifiers(newAssignments);
    } else {
      // Run validation on initial empty state
      updateValidation(assignments);
    }
  }, []);

  // Load class suggested traits when class changes
  useEffect(() => {
    if (characterData.class) {
      loadSuggestedTraits();
    }
  }, [characterData.class]);

  /**
   * Load suggested traits from class data
   */
  const loadSuggestedTraits = async () => {
    if (!characterData.class) return;

    try {
      const classData = await findClassById(characterData.class);
      if (classData?.suggestedTraits) {
        setSuggestedTraits(classData.suggestedTraits as unknown as Record<string, number>);
      }
    } catch (error) {
      console.error('Failed to load class suggested traits:', error);
    }
  };

  /**
   * Update remaining modifiers based on current assignments
   */
  const updateRemainingModifiers = (newAssignments: TraitAssignment) => {
    const remaining = calculateRemainingModifiers(newAssignments);
    setRemainingModifiers(remaining);
  };

  /**
   * Update validation state
   */
  const updateValidation = (newAssignments: TraitAssignment) => {
    const validationResult = validateTraitAssignments(newAssignments);
    setValidation(validationResult);
  };

  /**
   * Handle trait modifier selection
   */
  const handleAssignment = (trait: keyof TraitAssignment, modifier: number | null) => {
    const newAssignments: TraitAssignment = {
      ...assignments,
      [trait]: modifier
    };

    setAssignments(newAssignments);
    updateValidation(newAssignments);
    updateRemainingModifiers(newAssignments);

    // Update character data
    if (modifier !== null || Object.values(newAssignments).some(v => v !== null)) {
      onDataChange({
        traits: newAssignments as any
      });
    }
  };

  /**
   * Apply class suggested traits
   */
  const handleUseSuggested = () => {
    if (!suggestedTraits) return;

    const newAssignments: TraitAssignment = {
      agility: suggestedTraits.agility ?? null,
      strength: suggestedTraits.strength ?? null,
      finesse: suggestedTraits.finesse ?? null,
      instinct: suggestedTraits.instinct ?? null,
      presence: suggestedTraits.presence ?? null,
      knowledge: suggestedTraits.knowledge ?? null
    };

    setAssignments(newAssignments);
    updateValidation(newAssignments);
    updateRemainingModifiers(newAssignments);

    onDataChange({
      traits: newAssignments as any
    });
  };

  /**
   * Clear all assignments
   */
  const handleClearAll = () => {
    const newAssignments: TraitAssignment = {
      agility: null,
      strength: null,
      finesse: null,
      instinct: null,
      presence: null,
      knowledge: null
    };

    setAssignments(newAssignments);
    updateValidation(newAssignments);
    updateRemainingModifiers(newAssignments);

    onDataChange({ traits: undefined });
  };

  /**
   * Check if a modifier is available (not yet used)
   */
  const isModifierAvailable = (modifier: number): boolean => {
    return remainingModifiers.includes(modifier);
  };

  /**
   * Get available modifiers for dropdown
   * Show all remaining modifiers + the currently assigned value for this trait
   */
  const getAvailableModifiersForTrait = (trait: keyof TraitAssignment): (number | null)[] => {
    const currentValue = assignments[trait];
    const available = [...remainingModifiers];

    // Always include the current value if assigned, even if not in remaining
    if (currentValue !== null && !available.includes(currentValue)) {
      available.push(currentValue);
    }

    return [null, ...available];
  };

  return (
    <div className={styles.traitsAssignment}>
      <div className={styles.header}>
        <h2>Assign Your Character Traits</h2>
        <p>
          Distribute these modifiers across your six traits in any order:
        </p>
      </div>

      {/* Modifier Tracker */}
      <div className={styles.modifierTracker}>
        <div className={styles.modifierTrackerRemaining}>
          <strong>Remaining Modifiers:</strong>
          {remainingModifiers.length > 0 ? (
            <div className={styles.remainingChips}>
              {remainingModifiers.map((modifier, idx) => (
                <span key={`remaining-${modifier}-${idx}`} className={styles.modifierChip}>
                  {formatModifier(modifier)}
                </span>
              ))}
            </div>
          ) : (
            <span className={styles.complete}>✓ All assigned!</span>
          )}
        </div>
      </div>

      {/* Trait Cards */}
      <div className={styles.traitCards}>
        {getTraitKeys().map(trait => {
          const description = TRAIT_DESCRIPTIONS[trait];
          const currentValue = assignments[trait];
          const isSuggested =
            suggestedTraits && suggestedTraits[trait] === currentValue && currentValue !== null;

          return (
            <div
              key={trait}
              className={`${styles.traitCard} ${currentValue !== null ? styles.assigned : ''}`}
              role="group"
              aria-labelledby={`trait-${trait}-label`}
            >
              <div className={styles.traitCardHeader}>
                <label id={`trait-${trait}-label`} htmlFor={`trait-${trait}-select`}>
                  <strong>{description.name}</strong>
                  {isSuggested && <span className={styles.suggested} title="Suggested for your class"></span>}
                </label>
              </div>

              <div className={styles.selectWrapper}>
                <select
                  id={`trait-${trait}-select`}
                  value={currentValue?.toString() ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleAssignment(trait, value ? parseInt(value) : null);
                  }}
                  aria-label={`Assign modifier to ${description.name}`}
                  aria-describedby={`trait-${trait}-desc`}
                  className={styles.traitSelect}
                >
                  {getAvailableModifiersForTrait(trait).map((modifier, idx) => (
                    <option
                      key={`${trait}-modifier-${modifier}-${idx}`}
                      value={modifier?.toString() ?? ''}
                      disabled={modifier !== null && !isModifierAvailable(modifier) && currentValue !== modifier}
                    >
                      {modifier === null ? 'Select modifier' : formatModifier(modifier)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.traitActions}>
                {description.actions.map((action, idx) => (
                  <span key={idx} className={styles.action}>
                    {action}
                  </span>
                ))}
              </div>

              <p id={`trait-${trait}-desc`} className={styles.traitDescription}>
                {description.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Validation Messages - Show hints while incomplete, success when complete */}
      {validation.isValid ? (
        <div
          className={`${styles.validationMessage} ${styles.success}`}
          role="status"
          aria-live="polite"
        >
          <p>✓ All traits assigned! You're ready to continue.</p>
        </div>
      ) : (
        validation.hints.length > 0 && (
          <div
            className={`${styles.validationMessage} ${styles.hint}`}
            role="status"
            aria-live="polite"
          >
            {validation.hints.map((hint, idx) => (
              <p key={idx}>ℹ️ {hint}</p>
            ))}
          </div>
        )
      )}

      {/* Action Buttons */}
      <div className={styles.actions}>
        {suggestedTraits && (
          <button
            type="button"
            onClick={handleUseSuggested}
            className={styles.buttonSecondary}
            aria-label={`Use suggested traits for ${characterData.class}`}
          >
            Use Suggested
          </button>
        )}
        <button
          type="button"
          onClick={handleClearAll}
          className={styles.buttonSecondary}
          aria-label="Clear all trait assignments"
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

export default TraitsAssignment;
